import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import type { Collection } from "@shared/schemas/common";
import type {
  AddMemberRequest,
  CreateProductRequest,
  ProductDetail,
  ProductRow,
  UpdateProductRequest,
} from "@shared/schemas/product";
import {
  addProductMember,
  archiveProduct,
  createProduct,
  getProduct,
  listProducts,
  removeProductMember,
  updateProduct,
  type ProductListFilters,
} from "@client/lib/productsApi";
import { queryKeys } from "@client/lib/queryClient";

/**
 * Product reads and writes as hooks.
 *
 * Every mutation here writes the returned product straight into the detail cache
 * and then invalidates the lists. The server answers each mutation with the
 * whole resource (docs/06-api.md#conventions), so seeding the cache means the
 * page it returns to is already correct — and invalidating the lists afterwards
 * means the counts and orderings that only the list query knows still refresh.
 */

export function useProducts(filters: ProductListFilters): UseQueryResult<Collection<ProductRow>> {
  return useQuery({
    queryKey: queryKeys.products.list(filters),
    queryFn: () => listProducts(filters),
    // A filtered list keeps showing the previous result while the next one
    // loads, so typing in the search box does not blank the page on every key.
    placeholderData: (previous) => previous,
  });
}

export function useProduct(
  idOrSlug: string | undefined,
): UseQueryResult<ProductDetail> & { idOrSlug: string | undefined } {
  const result = useQuery({
    queryKey: queryKeys.products.detail(idOrSlug ?? ""),
    queryFn: () => getProduct(idOrSlug!),
    enabled: Boolean(idOrSlug),
  });
  return Object.assign(result, { idOrSlug });
}

/** Shared by every product mutation below: cache the resource, refresh the lists. */
function useProductWriteback() {
  const client = useQueryClient();
  return (product: ProductDetail) => {
    client.setQueryData(queryKeys.products.detail(product.slug), product);
    client.setQueryData(queryKeys.products.detail(product.id), product);
    void client.invalidateQueries({ queryKey: queryKeys.products.all });
  };
}

export function useCreateProduct() {
  const writeBack = useProductWriteback();
  return useMutation({
    mutationFn: (input: CreateProductRequest) => createProduct(input),
    onSuccess: writeBack,
  });
}

export function useUpdateProduct(id: string) {
  const writeBack = useProductWriteback();
  return useMutation({
    mutationFn: (input: UpdateProductRequest) => updateProduct(id, input),
    onSuccess: writeBack,
  });
}

export function useArchiveProduct(id: string) {
  const writeBack = useProductWriteback();
  return useMutation({
    mutationFn: (restore: boolean) => archiveProduct(id, restore),
    onSuccess: writeBack,
  });
}

export function useAddProductMember(id: string) {
  const writeBack = useProductWriteback();
  return useMutation({
    mutationFn: (input: AddMemberRequest) => addProductMember(id, input),
    onSuccess: writeBack,
  });
}

export function useRemoveProductMember(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => removeProductMember(id, userId),
    // A 204 carries no resource to cache, so this is the one that has to refetch.
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.products.all }),
  });
}
